import { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  TextField, 
  Button, 
  Box, 
  Paper,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Autocomplete,
  Chip,
  Stack
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { authService } from '../../services/authService';
import AddIcon from '@mui/icons-material/Add';

function EditOffre() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [entreprises, setEntreprises] = useState([]);
  const [technologies, setTechnologies] = useState([]);
  const [selectedTechnologies, setSelectedTechnologies] = useState([]);
  const [newTechnology, setNewTechnology] = useState('');
  
  const [formData, setFormData] = useState({
    titre: '',
    description: '',
    entreprise_id: '',
    duree: '',
    remuneration: '',
    dateDebut: '',
    localisation: '',
    type: 'stage'
  });

  // Ajout des options de durée
  const dureeOptions = [
    '1 mois',
    '2 mois',
    '3 mois',
    '4 mois',
    '5 mois',
    '6 mois'
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Charger l'offre
        const offreResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/offres/${id}`, {
          headers: {
            'Authorization': `Bearer ${authService.getToken()}`,
            'Accept': 'application/json'
          }
        });

        if (!offreResponse.ok) {
          throw new Error('Erreur lors du chargement de l\'offre');
        }

        const offreData = await offreResponse.json();
        const offre = offreData.offre;

        setFormData({
          titre: offre.titre,
          description: offre.description,
          entreprise_id: offre.entreprises[0]?.id_entreprises,
          duree: offre.duree,
          remuneration: Math.round(offre.remuneration),
          dateDebut: offre.dateDebut.split('T')[0],
          localisation: offre.localisation,
          type: 'stage'
        });

        setSelectedTechnologies(offre.technologies);

        // Charger la liste des entreprises
        const entreprisesResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/entreprises`, {
          headers: {
            'Authorization': `Bearer ${authService.getToken()}`,
            'Accept': 'application/json'
          }
        });

        if (entreprisesResponse.ok) {
          const data = await entreprisesResponse.json();
          setEntreprises(data.data || []);
        }

        // Charger la liste des technologies
        const technologiesResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/offres/${id}`, {
          headers: {
            'Authorization': `Bearer ${authService.getToken()}`,
            'Accept': 'application/json'
          }
        });

        if (technologiesResponse.ok) {
          const data = await technologiesResponse.json();
          setTechnologies(data.offre.technologies || []);
        }

      } catch (err) {
        setError('Erreur lors du chargement des données');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
  
    try {
      const offreData = {
        ...formData,
        technologies: selectedTechnologies.map(tech => tech.nom_techno) // On envoie juste les noms des technologies
      };
  
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/offres/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${authService.getToken()}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(offreData)
      });
  
      if (!response.ok) {
        throw new Error('Erreur lors de la modification de l\'offre');
      }
  
      setSuccess(true);
      setTimeout(() => {
        navigate('/offres');
      }, 2000);
    } catch (err) {
      setError("Une erreur est survenue lors de la modification de l'offre");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/offres/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${authService.getToken()}`,
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la suppression de l\'offre');
      }

      setOpenDeleteDialog(false);
      navigate('/offres', { state: { message: 'Offre supprimée avec succès' } });
    } catch (err) {
      setError("Une erreur est survenue lors de la suppression de l'offre");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddTechnology = () => {
    if (newTechnology.trim()) {
      const newTech = {
        nom_techno: newTechnology.trim()
      };
      
      if (!selectedTechnologies.some(tech => tech.nom_techno === newTech.nom_techno)) {
        setSelectedTechnologies(prev => [...prev, newTech]);
      }
      setNewTechnology('');
    }
  };

  const handleDeleteTechnology = (techToDelete) => {
    setSelectedTechnologies(prev => 
      prev.filter(tech => tech.nom_techno !== techToDelete.nom_techno)
    );
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper sx={{ p: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/offres')}
          >
            Retour aux offres
          </Button>
        </Box>

        <Typography variant="h4" gutterBottom>
          Modifier l'offre
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Offre modifiée avec succès !
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Titre"
            name="titre"
            value={formData.titre}
            onChange={handleChange}
            required
            margin="normal"
          />

          <TextField
            fullWidth
            label="Description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            required
            multiline
            rows={4}
            margin="normal"
          />

          <FormControl fullWidth margin="normal">
            <InputLabel>Entreprise</InputLabel>
            <Select
              name="entreprise_id"
              value={formData.entreprise_id || ''}
              onChange={handleChange}
              required
              label="Entreprise"
            >
              {entreprises && entreprises.map((entreprise) => (
                <MenuItem 
                  key={entreprise.id_entreprises} 
                  value={entreprise.id_entreprises}
                >
                  {entreprise.nom}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Technologies requises
            </Typography>
            <Stack 
              direction="row" 
              spacing={1} 
              sx={{ 
                mb: 2,
                py: 2,
                gap: 1.5,
                flexWrap: 'wrap'
              }}
            >
              {selectedTechnologies.map((tech, index) => (
                <Chip
                  key={index}
                  label={tech.nom_techno}
                  onDelete={() => handleDeleteTechnology(tech)}
                  color="primary"
                  variant="outlined"
                  sx={{
                    fontSize: '1rem',
                    height: 36,
                    '& .MuiChip-label': {
                      px: 2
                    }
                  }}
                />
              ))}
            </Stack>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                fullWidth
                label="Nouvelle technologie"
                value={newTechnology}
                onChange={(e) => setNewTechnology(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddTechnology();
                  }
                }}
                size="small"
              />
              <Button
                startIcon={<AddIcon />}
                onClick={handleAddTechnology}
                variant="contained"
                disabled={!newTechnology.trim()}
              >
                Ajouter
              </Button>
            </Box>
          </Box>

          <FormControl fullWidth margin="normal">
            <InputLabel>Durée du stage</InputLabel>
            <Select
              name="duree"
              value={formData.duree}
              onChange={handleChange}
              required
              label="Durée du stage"
            >
              {dureeOptions.map((duree) => (
                <MenuItem key={duree} value={duree}>
                  {duree}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Rémunération"
            name="remuneration"
            value={formData.remuneration}
            onChange={(e) => {
              const value = e.target.value;
              setFormData(prev => ({
                ...prev,
                remuneration: Math.round(Number(value))
              }));
            }}
            required
            margin="normal"
            type="number"
            inputProps={{ step: "1" }}
          />

          <TextField
            fullWidth
            label="Date de début"
            name="dateDebut"
            type="date"
            value={formData.dateDebut}
            onChange={handleChange}
            required
            margin="normal"
            InputLabelProps={{
              shrink: true,
            }}
          />

          <TextField
            fullWidth
            label="Localisation"
            name="localisation"
            value={formData.localisation}
            onChange={handleChange}
            required
            margin="normal"
          />

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between', mt: 4 }}>
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setOpenDeleteDialog(true)}
              disabled={loading}
            >
              Supprimer l'offre
            </Button>
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/offres')}
                disabled={loading}
              >
                Annuler
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
              >
                {loading ? 'Modification...' : 'Modifier l\'offre'}
              </Button>
            </Box>
          </Box>
        </Box>

        <Dialog
          open={openDeleteDialog}
          onClose={() => setOpenDeleteDialog(false)}
        >
          <DialogTitle>
            Confirmer la suppression
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              Êtes-vous sûr de vouloir supprimer cette offre ? Cette action est irréversible.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => setOpenDeleteDialog(false)}
              disabled={loading}
            >
              Annuler
            </Button>
            <Button 
              onClick={handleDelete}
              color="error"
              disabled={loading}
              variant="contained"
            >
              {loading ? 'Suppression...' : 'Supprimer'}
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </Container>
  );
}

export default EditOffre; 